import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './AprovarClientesDetalhes.css';
import Sidebar from '../../components/Sidebar/Sidebar';
import Topbar from '../../components/Topbar/Topbar';
import { API_URL } from '../../api';
import { mascaraCNPJ, mascaraTelefone } from '../../utils/mascaras';
import { Edit, Check, X, ArrowLeft, AlertTriangle } from 'lucide-react';

function AprovarClientesDetalhes() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [menuAberto, setMenuAberto] = useState(false);
  const [cliente, setCliente] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [modalAberto, setModalAberto] = useState(false);
  const [acaoModal, setAcaoModal] = useState('');
  const [motivoReprovacao, setMotivoReprovacao] = useState('');

  useEffect(() => {
    async function fetchCliente() {
      setCarregando(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/clientes-pre-cadastro/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Erro ao buscar dados do cliente');
        }

        const data = await response.json();
        setCliente(data);
        setErro('');
      } catch (error) {
        console.error('Erro:', error);
        setErro('Não foi possível carregar os dados do cliente. Por favor, tente novamente.');
        
        // Dados simulados para desenvolvimento
        if (process.env.NODE_ENV === 'development') {
          setCliente({
            id: id,
            nome: 'Empresa Exemplo LTDA',
            cnpj: '12.345.678/0001-90',
            email: 'contato@empresaexemplo.com.br',
            telefone_1: '(11) 98765-4321',
            telefone_2: '(11) 3456-7890',
            cep: '01234-567',
            endereco: 'Rua Exemplo',
            numero: '123',
            complemento: 'Sala 45',
            bairro: 'Centro',
            cidade: 'São Paulo',
            estado: 'SP',
            data_cadastro: '2023-05-15T10:30:00',
            status: 'pendente'
          });
        }
      } finally {
        setCarregando(false);
      }
    }

    fetchCliente();
  }, [id]);

  const handleVoltar = () => {
    navigate('/aprovar-clientes');
  };

  const handleEditar = () => {
    navigate(`/aprovar-cliente/editar/${id}`);
  };

  const abrirModal = (acao) => {
    setAcaoModal(acao);
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setMotivoReprovacao('');
  };

  const handleAprovarCliente = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/clientes-pre-cadastro/${id}/aprovar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao aprovar cliente');
      }

      fecharModal();
      navigate('/aprovar-clientes', { state: { mensagem: 'Cliente aprovado com sucesso!' } });
    } catch (error) {
      console.error('Erro:', error);
      setErro('Não foi possível aprovar o cliente. Por favor, tente novamente.');
      fecharModal();
    }
  };

  const handleReprovarCliente = async () => {
    if (!motivoReprovacao.trim()) {
      setErro('Por favor, informe o motivo da reprovação.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/clientes-pre-cadastro/${id}/reprovar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ motivo: motivoReprovacao })
      });

      if (!response.ok) {
        throw new Error('Erro ao reprovar cliente');
      }

      fecharModal();
      navigate('/aprovar-clientes', { state: { mensagem: 'Cliente reprovado com sucesso!' } });
    } catch (error) {
      console.error('Erro:', error);
      setErro('Não foi possível reprovar o cliente. Por favor, tente novamente.');
      fecharModal();
    }
  };

  const formatarData = (dataString) => {
    if (!dataString) return '';
    const data = new Date(dataString);
    return data.toLocaleDateString('pt-BR') + ' ' + data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="dashboard-root pg-detalhes-pre-cadastro">
      <Sidebar menuAberto={menuAberto} setMenuAberto={setMenuAberto} />
      <div className="main-content">
        <Topbar
          setMenuAberto={setMenuAberto}
          titulo="Detalhes do Cliente Pendente"
        />
        <div className="conteudo-dashboard">
          {carregando ? (
            <div className="loading-container">Carregando...</div>
          ) : erro ? (
            <div className="error-container">
              <AlertTriangle size={24} />
              <p>{erro}</p>
            </div>
          ) : cliente ? (
            <>
            <div className="card-page-main p-relative">
              <div className="card-page-main-header">
                <div className="header-left">
                  <button className="btn-voltar" onClick={handleVoltar}>
                    <ArrowLeft size={16} style={{ marginRight: 8 }} />
                    Voltar para a listagem
                  </button>
                  <h2>Detalhes do Cliente</h2>
                </div>
                <div className="header-actions">
                  <button 
                    className="btn btn-md btn-primary"
                    onClick={handleEditar}
                  >
                    <Edit size={18} style={{ marginRight: 6 }} /> Editar Cliente
                  </button>
                </div>
              </div>

              <div className="cliente-detalhes-content">
                <div className="detalhes-section">
                  <h3 className="section-title">Informações Gerais</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Razão Social</label>
                      <div className="campo-valor">{cliente.razao_social || cliente.nome}</div>
                    </div>
                    <div className="form-group">
                      <label>Nome Fantasia</label>
                      <div className="campo-valor">{cliente.nome_fantasia || '-'}</div>
                    </div>
                    <div className="form-group">
                      <label>CNPJ</label>
                      <div className="campo-valor">{mascaraCNPJ(cliente.cnpj)}</div>
                    </div>
                    <div className="form-group">
                      <label>Data de Cadastro</label>
                      <div className="campo-valor">{formatarData(cliente.data_cadastro)}</div>
                    </div>
                    <div className="form-group">
                      <label>Telefone 1</label>
                      <div className="campo-valor">{mascaraTelefone(cliente.telefone_1)}</div>
                    </div>
                    <div className="form-group">
                      <label>Telefone 2</label>
                      <div className="campo-valor">{mascaraTelefone(cliente.telefone_2) || '-'}</div>
                    </div>
                  </div>
                </div>

                <div className="detalhes-section">
                  <h3 className="section-title">Informações de Contato</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>E-mail Principal</label>
                      <div className="campo-valor">{cliente.email || cliente.email_1 || '-'}</div>
                    </div>
                    <div className="form-group">
                      <label>E-mail Secundário</label>
                      <div className="campo-valor">{cliente.email_2 || '-'}</div>
                    </div>
                    <div className="form-group">
                      <label>E-mail Cobrança</label>
                      <div className="campo-valor">{cliente.email_cobranca || '-'}</div>
                    </div>
                    <div className="form-group">
                      <label>E-mail XML</label>
                      <div className="campo-valor">{cliente.email_xml || '-'}</div>
                    </div>
                    <div className="form-group">
                      <label>Nome do Contato</label>
                      <div className="campo-valor">{cliente.contato || '-'}</div>
                    </div>
                  </div>
                </div>

                <div className="detalhes-section">
                  <h3 className="section-title">Endereço</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>CEP</label>
                      <div className="campo-valor">{cliente.cep}</div>
                    </div>
                    <div className="form-group">
                      <label>Logradouro</label>
                      <div className="campo-valor">{cliente.logradouro || cliente.endereco}</div>
                    </div>
                    <div className="form-group">
                      <label>Número</label>
                      <div className="campo-valor">{cliente.numero}</div>
                    </div>
                    <div className="form-group">
                      <label>Complemento</label>
                      <div className="campo-valor">{cliente.complemento || '-'}</div>
                    </div>
                    <div className="form-group">
                      <label>Bairro</label>
                      <div className="campo-valor">{cliente.bairro}</div>
                    </div>
                    <div className="form-group">
                      <label>Cidade</label>
                      <div className="campo-valor">{cliente.cidade}</div>
                    </div>
                    <div className="form-group">
                      <label>Estado</label>
                      <div className="campo-valor">{cliente.estado}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='pg-detalhes-pre-cadastro card-page-main mt-2'>
                <div className="header-left mb-1">
                  <h2>Aprovação do Cadastro</h2>
                </div>
                <div className="cards-list">
                  <div className="card-aprovacao">
                    <div className="card-header">
                      <h4 className="card-title">Financeiro</h4>
                      <span className="status-badge status-aprovado">Aprovado</span>
                    </div>
                    <div className="card-body">
                      <p className="card-name">Maria Silva</p>
                      <p className="card-detail">Aprovado em: 24/09/2025 10:15</p>
                    </div>
                  </div>

                  <div className="card-aprovacao">
                    <div className="card-header">
                      <h4 className="card-title">Fiscal</h4>
                      <span className="status-badge status-reprovado">Reprovado</span>
                    </div>
                    <div className="card-body">
                      <p className="card-name">Carlos Mendes</p>
                      <p className="card-detail">Motivo: Documentação incompleta</p>
                      <p className="card-detail">Reprovado em: 25/09/2025 09:30</p>
                    </div>
                  </div>

                  <div className="card-aprovacao">
                    <div className="card-header">
                      <h4 className="card-title">Comercial</h4>
                      <span className="status-badge status-pendente">Pendente</span>
                    </div>
                    <div className="card-body">
                      <div className="card-actions">
                        <button className="btn btn-aprovar-cliente" onClick={() => abrirModal('aprovar')}>Aprovar</button>
                        <button className="btn btn-reprovar-cliente" onClick={() => abrirModal('reprovar')}>Reprovar</button>
                      </div>
                    </div>
                  </div>

                  <div className="card-aprovacao">
                    <div className="card-header">
                      <h4 className="card-title">Operações</h4>
                      <span className="status-badge status-pendente">Pendente</span>
                    </div>
                    <div className="card-body">
                      <div className="card-actions">
                        <button className="btn btn-aprovar-cliente" onClick={() => abrirModal('aprovar')}>Aprovar</button>
                        <button className="btn btn-reprovar-cliente" onClick={() => abrirModal('reprovar')}>Reprovar</button>
                      </div>
                    </div>
                  </div>

                  <div className="card-aprovacao">
                    <div className="card-header">
                      <h4 className="card-title">Compliance</h4>
                      <span className="status-badge status-pendente">Pendente</span>
                    </div>
                    <div className="card-body">
                      <div className="card-actions">
                        <button className="btn btn-aprovar-cliente" onClick={() => abrirModal('aprovar')}>Aprovar</button>
                        <button className="btn btn-reprovar-cliente" onClick={() => abrirModal('reprovar')}>Reprovar</button>
                      </div>
                    </div>
                  </div>
                </div>

            </div>
            </>
            
          ) : (
            <div className="error-container">
              <AlertTriangle size={24} />
              <p>Cliente não encontrado</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Confirmação */}
      {modalAberto && (
        <div className="modal-overlay">
          <div className="modal-confirmar">
            <div className="modal-header">
              <h3>{acaoModal === 'aprovar' ? 'Confirmar Aprovação' : 'Confirmar Reprovação'}</h3>
              <button 
                className="modal-fechar" 
                onClick={fecharModal}
                aria-label="Fechar"
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {acaoModal === 'aprovar' ? (
                <>
                  <p>Tem certeza que deseja aprovar o cliente <strong>{cliente?.razao_social || cliente?.nome}</strong>?</p>
                  <p>Esta ação não pode ser desfeita.</p>
                </>
              ) : (
                <>
                  <p>Por favor, informe o motivo da reprovação:</p>
                  <textarea
                    value={motivoReprovacao}
                    onChange={(e) => setMotivoReprovacao(e.target.value)}
                    placeholder="Motivo da reprovação"
                    rows={4}
                    className="textarea-motivo"
                    required
                  />
                </>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="btn-cancelar" 
                onClick={fecharModal}
              >
                Cancelar
              </button>
              <button 
                className="btn btn-md btn-primary" 
                onClick={acaoModal === 'aprovar' ? handleAprovarCliente : handleReprovarCliente}
              >
                {acaoModal === 'aprovar' ? 'Confirmar Aprovação' : 'Confirmar Reprovação'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AprovarClientesDetalhes;